import { Effect, Schema } from "effect";

import type { Fingerprint } from "../fingerprint";
import type { GitHubIssueId } from "../github/ids";
import type {
  ClusterName,
  ControllerKind,
  ControllerName,
  KubeSystemUid,
  NamespaceName,
  PodName,
} from "../k8s/ids";
import type { Cores, Bytes, Millicores } from "../units";

import {
  Percentage,
  Multiplier,
  RestartCount,
} from "../units";
import {
  ClusterId,
  ConnectionId,
  SentryConnectionId,
} from "./ids";

export const AgentTypes = {
  AzureDevOpsPipeline: "azure-devops-pipeline",
  GitHubActions: "github-actions",
  K8sIssues: "k8s-issues",
  K8sOptimization: "k8s-optimization",
  OtelReceiver: "otel-receiver",
  Sentry: "sentry",
} as const;

export type AgentTypeKey = keyof typeof AgentTypes;
export type AgentTypeValue =
  (typeof AgentTypes)[AgentTypeKey];

export const agentTypes = Object.values(AgentTypes);

// Default per-agent `branches` config: short names, normalized before comparing.
export const DEFAULT_MONITORED_BRANCHES = [
  "main",
  "master",
  "test",
  "develop",
  "staging",
  "production",
] as const;

// Strip the `refs/heads/` prefix so short names and full refs compare equal.
export const normalizeBranch = (branch: string): string =>
  branch.replace(/^refs\/heads\//, "");

export const AgentTypesSchema = Schema.Literals([
  AgentTypes.K8sIssues,
  AgentTypes.K8sOptimization,
  AgentTypes.AzureDevOpsPipeline,
  AgentTypes.GitHubActions,
  AgentTypes.OtelReceiver,
  AgentTypes.Sentry,
]);

export const AgentRunStatus = {
  Pending: "pending",
  Running: "running",
  Completed: "completed",
  Failed: "failed",
} as const;

export type AgentRunStatusKey = keyof typeof AgentRunStatus;
export type AgentRunStatusValue =
  (typeof AgentRunStatus)[AgentRunStatusKey];

export const agentRunStatuses =
  Object.values(AgentRunStatus);

export const AgentRunStatusSchema = Schema.Literals([
  AgentRunStatus.Pending,
  AgentRunStatus.Running,
  AgentRunStatus.Completed,
  AgentRunStatus.Failed,
]);

/**
 * Agent run outcome - indicates the result of a completed run
 * - success: All operations completed without errors
 * - partial: Some operations failed but run completed (e.g., one cluster timed out)
 * - failure: Run failed entirely
 */
export const AgentRunOutcome = {
  Success: "success",
  Partial: "partial",
  Failure: "failure",
} as const;

export type AgentRunOutcomeKey =
  keyof typeof AgentRunOutcome;
export type AgentRunOutcomeValue =
  (typeof AgentRunOutcome)[AgentRunOutcomeKey];

export const agentRunOutcomes =
  Object.values(AgentRunOutcome);

export const AgentRunOutcomeSchema = Schema.Literals([
  AgentRunOutcome.Success,
  AgentRunOutcome.Partial,
  AgentRunOutcome.Failure,
]);

/**
 * Error that occurred during an agent run
 */
export type AgentRunError = {
  /** Source of the error (e.g., "cluster:bip-02", "project:my-app") */
  source: string;
  /** Error message */
  message: string;
  /** ISO timestamp when the error occurred */
  timestamp: string;
};

/**
 * Statistics about items processed during an agent run
 */
export type AgentRunStats = {
  /** Total number of items processed (clusters/pipelines/projects) */
  processed: number;
  /** Number of successfully processed items */
  successful: number;
  /** Number of failed items */
  failed: number;
};

/**
 * Statistics about skipped items during an agent run
 * Tracks different reasons why items were skipped (duplicates, rate limits, etc.)
 */
export type SkipStats = {
  /** Number of items skipped due to being duplicates */
  duplicates: number;
  /** Number of items skipped due to suppression rules */
  suppressed: number;
  /** Number of items skipped due to errors */
  errors: number;
  /** Number of items skipped due to rate limiting */
  rateLimited: number;
};

export type DuplicateIssueIds = Record<
  Fingerprint,
  GitHubIssueId
>;

/**
 * Base type for all agent results - provides consistent structure
 */
export type BaseAgentResult = {
  /** Human-readable summary of the run */
  summary: string;
  /** ISO timestamp when data was collected */
  collectedAt: string;
  /** Statistics about processed items */
  stats: AgentRunStats;
  /** Errors that occurred during the run (optional) */
  errors?: AgentRunError[];
};

export const SchedulePresets = {
  Manual: "0 0 31 2 *", // Feb 31st - never runs (manual only)
  EveryMinute: "*/1 * * * *",
  Every5Minutes: "*/5 * * * *",
  EveryHour: "0 * * * *",
  Every6Hours: "0 */6 * * *",
  Every12Hours: "0 */12 * * *",
  Daily: "0 0 * * *",
} as const;

export type SchedulePresetKey =
  keyof typeof SchedulePresets;
export type SchedulePresetValue =
  (typeof SchedulePresets)[SchedulePresetKey];

export const schedulePresets =
  Object.values(SchedulePresets);

// K8s Issues Agent Configuration (realtime monitoring)
const K8sIssuesThresholdsSchema = Schema.Struct({
  cpuUtilization: Percentage.pipe(
    Schema.withDecodingDefaultType(
      Effect.sync(() => Percentage.make(80))
    ),
    Schema.withConstructorDefault(
      Effect.sync(() => Percentage.make(80))
    )
  ),
  memoryUtilization: Percentage.pipe(
    Schema.withDecodingDefaultType(
      Effect.sync(() => Percentage.make(80))
    ),
    Schema.withConstructorDefault(
      Effect.sync(() => Percentage.make(80))
    )
  ),
  restartCount: RestartCount.pipe(
    Schema.withDecodingDefaultType(
      Effect.sync(() => RestartCount.make(5))
    ),
    Schema.withConstructorDefault(
      Effect.sync(() => RestartCount.make(5))
    )
  ),
});

export const K8sIssuesAgentConfigSchema = Schema.Struct({
  clusterIds: Schema.mutable(
    Schema.NonEmptyArray(ClusterId)
  ),
  namespaces: Schema.mutable(
    Schema.Array(Schema.String)
  ).pipe(
    Schema.withDecodingDefaultType(
      Effect.sync(() => [] as string[])
    ),
    Schema.withConstructorDefault(
      Effect.sync(() => [] as string[])
    )
  ),
  thresholds: K8sIssuesThresholdsSchema,
});

export type K8sIssuesAgentConfig =
  typeof K8sIssuesAgentConfigSchema.Type;

// K8s Optimization Agent Configuration (daily analysis)
const K8sOptimizationThresholdsSchema = Schema.Struct({
  overprovisionSavingsMultiplier: Multiplier.pipe(
    Schema.withDecodingDefaultType(
      Effect.sync(() => Multiplier.make(2))
    ),
    Schema.withConstructorDefault(
      Effect.sync(() => Multiplier.make(2))
    )
  ),
});

export const K8sOptimizationAgentConfigSchema =
  Schema.Struct({
    clusterIds: Schema.mutable(
      Schema.NonEmptyArray(ClusterId)
    ),
    namespaces: Schema.mutable(
      Schema.Array(Schema.String)
    ).pipe(
      Schema.withDecodingDefaultType(
        Effect.sync(() => [] as string[])
      ),
      Schema.withConstructorDefault(
        Effect.sync(() => [] as string[])
      )
    ),
    thresholds: K8sOptimizationThresholdsSchema,
  });

export type K8sOptimizationAgentConfig =
  typeof K8sOptimizationAgentConfigSchema.Type;

// Azure DevOps Pipeline Monitor Agent Configuration
const AzureDevOpsProjectSchema = Schema.Struct({
  projectName: Schema.NonEmptyString,
  pipelineIds: Schema.mutable(
    Schema.NonEmptyArray(Schema.Finite)
  ),
});

export const AzureDevOpsPipelineAgentConfigSchema =
  Schema.Struct({
    branches: Schema.mutable(
      Schema.NonEmptyArray(Schema.NonEmptyString)
    ).pipe(
      Schema.withDecodingDefaultType(
        Effect.sync(
          () => [...DEFAULT_MONITORED_BRANCHES] as const
        )
      ),
      Schema.withConstructorDefault(
        Effect.sync(
          () => [...DEFAULT_MONITORED_BRANCHES] as const
        )
      )
    ),
    connectionId: ConnectionId,
    initialLookbackHours: Schema.Finite.pipe(
      Schema.check(
        Schema.isGreaterThanOrEqualTo(1),
        Schema.isLessThanOrEqualTo(168)
      ),
      Schema.withDecodingDefaultType(Effect.succeed(24)),
      Schema.withConstructorDefault(Effect.succeed(24))
    ),
    projects: Schema.mutable(
      Schema.NonEmptyArray(AzureDevOpsProjectSchema)
    ),
  });

export type AzureDevOpsPipelineAgentConfig =
  typeof AzureDevOpsPipelineAgentConfigSchema.Type;

// Sentry Issues Agent Configuration
const SentryEnabledChecksSchema = Schema.Struct({
  newErrors: Schema.Boolean,
  performanceIssues: Schema.Boolean.pipe(
    Schema.withDecodingDefaultType(Effect.succeed(false)),
    Schema.withConstructorDefault(Effect.succeed(false))
  ),
  regressions: Schema.Boolean,
  cronMonitorFailures: Schema.Boolean.pipe(
    Schema.withDecodingDefaultType(Effect.succeed(false)),
    Schema.withConstructorDefault(Effect.succeed(false))
  ),
});

export const SentryAgentConfigSchema = Schema.Struct({
  connectionId: SentryConnectionId,
  projectSlugs: Schema.mutable(
    Schema.NonEmptyArray(Schema.NonEmptyString)
  ),
  projectEnvironments: Schema.Record(
    Schema.String,
    Schema.mutable(Schema.Array(Schema.String))
  ).pipe(
    Schema.withDecodingDefaultType(Effect.sync(() => ({}))),
    Schema.withConstructorDefault(Effect.sync(() => ({})))
  ),
  enabledChecks: SentryEnabledChecksSchema,
  initialLookbackHours: Schema.Finite.pipe(
    Schema.check(
      Schema.isGreaterThanOrEqualTo(1),
      Schema.isLessThanOrEqualTo(168)
    ),
    Schema.withDecodingDefaultType(Effect.succeed(72)),
    Schema.withConstructorDefault(Effect.succeed(72))
  ),
  customQueries: Schema.Struct({
    newErrors: Schema.optional(Schema.String),
    performanceIssues: Schema.optional(Schema.String),
    regressions: Schema.optional(Schema.String),
    cronMonitorFailures: Schema.optional(Schema.String),
  }).pipe(
    Schema.withDecodingDefaultType(Effect.sync(() => ({}))),
    Schema.withConstructorDefault(Effect.sync(() => ({})))
  ),
  overrideBaseFilters: Schema.Struct({
    newErrors: Schema.optional(Schema.Boolean),
    performanceIssues: Schema.optional(Schema.Boolean),
    regressions: Schema.optional(Schema.Boolean),
    cronMonitorFailures: Schema.optional(Schema.Boolean),
  }).pipe(
    Schema.withDecodingDefaultType(Effect.sync(() => ({}))),
    Schema.withConstructorDefault(Effect.sync(() => ({})))
  ),
});

export type SentryAgentConfig =
  typeof SentryAgentConfigSchema.Type;

// OTel Receiver Agent Configuration (push-based)
export const OtelReceiverAgentConfigSchema = Schema.Struct({
  apiKey: Schema.mutableKey(Schema.optional(Schema.String)),
});

export type OtelReceiverAgentConfig =
  typeof OtelReceiverAgentConfigSchema.Type;

// GitHub Actions Workflow Monitor Agent Configuration
export const GitHubActionsAgentConfigSchema = Schema.Struct(
  {
    branches: Schema.mutable(
      Schema.NonEmptyArray(Schema.NonEmptyString)
    ).pipe(
      Schema.withDecodingDefaultType(
        Effect.sync(
          () => [...DEFAULT_MONITORED_BRANCHES] as const
        )
      ),
      Schema.withConstructorDefault(
        Effect.sync(
          () => [...DEFAULT_MONITORED_BRANCHES] as const
        )
      )
    ),
    initialLookbackHours: Schema.Finite.pipe(
      Schema.check(
        Schema.isGreaterThanOrEqualTo(1),
        Schema.isLessThanOrEqualTo(168)
      ),
      Schema.withDecodingDefaultType(Effect.succeed(24)),
      Schema.withConstructorDefault(Effect.succeed(24))
    ),
  }
);

export type GitHubActionsAgentConfig =
  typeof GitHubActionsAgentConfigSchema.Type;

// Union of all agent configurations
export const AgentConfigurationSchema = Schema.Union([
  K8sIssuesAgentConfigSchema,
  K8sOptimizationAgentConfigSchema,
  AzureDevOpsPipelineAgentConfigSchema,
  GitHubActionsAgentConfigSchema,
  OtelReceiverAgentConfigSchema,
  SentryAgentConfigSchema,
]);

export type AgentConfiguration =
  typeof AgentConfigurationSchema.Type;

/** Maps AgentTypes value → its specific configuration type */
export type AgentConfigMap = {
  [AgentTypes.AzureDevOpsPipeline]: AzureDevOpsPipelineAgentConfig;
  [AgentTypes.GitHubActions]: GitHubActionsAgentConfig;
  [AgentTypes.K8sIssues]: K8sIssuesAgentConfig;
  [AgentTypes.K8sOptimization]: K8sOptimizationAgentConfig;
  [AgentTypes.OtelReceiver]: OtelReceiverAgentConfig;
  [AgentTypes.Sentry]: SentryAgentConfig;
};

/**
 * Maps AgentTypes value → its specific configuration Schema.
 * Use this for type-discriminated validation instead of the AgentConfigurationSchema union,
 * which can incorrectly match permissive schemas (e.g., GitHubActions) and strip fields.
 */
export const AgentConfigSchemaMap = {
  [AgentTypes.AzureDevOpsPipeline]:
    AzureDevOpsPipelineAgentConfigSchema,
  [AgentTypes.GitHubActions]:
    GitHubActionsAgentConfigSchema,
  [AgentTypes.K8sIssues]: K8sIssuesAgentConfigSchema,
  [AgentTypes.K8sOptimization]:
    K8sOptimizationAgentConfigSchema,
  [AgentTypes.OtelReceiver]: OtelReceiverAgentConfigSchema,
  [AgentTypes.Sentry]: SentryAgentConfigSchema,
} as const;

/**
 * Resource type for K8s issues
 */
export type K8sIssueResourceType =
  | "pod"
  | "node"
  | "volume";

/**
 * Structured table data for issue details.
 * Used for displaying tabular data like over-provisioned pods.
 */
export type IssueDetailTable = {
  /** Column headers */
  headers: string[];
  /** Data rows - each row is an array of cell values */
  rows: string[][];
};

/**
 * K8s issue detected by the agent.
 * Shared between agent-runner, @vivus/agents, and frontend.
 */
export type K8sIssue = {
  /** Cluster ID where the issue was detected */
  clusterId: string;
  /** Controller kind (Deployment, ReplicaSet, Job, CronJob, etc.) */
  controllerKind?: ControllerKind | null;
  /** Controller name for stable fingerprinting (e.g., deployment name without pod suffix) */
  controllerName?: ControllerName | null;
  /** Structured table data for detailed information */
  detailTable?: IssueDetailTable;
  /** Pod logs/events for context */
  logs?: string;
  message: string;
  namespace: NamespaceName;
  recommendation: string;
  /** Full resource name (e.g., pod name with suffix) */
  resource: string;
  /** Type of resource. Defaults to "pod" for backward compatibility */
  resourceType?: K8sIssueResourceType;
  severity: "critical" | "warning" | "info";
};

/**
 * Per-cluster metrics and issues result
 */
export type K8sClusterResult = {
  clusterId: string;
  clusterName: ClusterName;
  collectedAt: string;
  /** Kubernetes kube-system namespace UID - used for stable fingerprinting */
  kubeSystemUid: KubeSystemUid;
  namespaces: Array<{
    namespace: NamespaceName;
    pods: Array<{
      name: PodName;
      cpuUsage: number;
      memoryUsage: number;
      restartCount: number;
      status: string;
      /** K8s pod phase (Running, Pending, Terminating, etc.) */
      phase?: string;
      /** Controller kind (ReplicaSet, DaemonSet, StatefulSet, Job, etc.) */
      controllerKind?: ControllerKind | null;
      /** Controller name (exact name from K8s API) */
      controllerName?: ControllerName | null;
      /** CPU request in cores (null if not set) */
      cpuRequest?: number | null;
      /** CPU limit in cores (null if not set) */
      cpuLimit?: number | null;
      /** Memory request in bytes (null if not set) */
      memoryRequest?: number | null;
      /** Memory limit in bytes (null if not set) */
      memoryLimit?: number | null;
      /** Suggested CPU limit based on historical usage (cores) */
      suggestedCpuLimit?: number | null;
      /** Suggested memory limit based on historical usage (bytes) */
      suggestedMemoryLimit?: number | null;
    }>;
    totalCpu: number;
    totalMemory: number;
  }>;
  nodes?: Array<{
    name: string;
    diskUsagePercent: Percentage | null;
    imageCount: number;
    isReady: boolean;
    hasDiskPressure: boolean;
    hasMemoryPressure: boolean;
  }>;
  volumes?: Array<{
    name: string;
    namespace: NamespaceName;
    pvcPhase: string;
    storageClass: string | null;
    isHealthy: boolean;
    healthIssue: string | null;
  }>;
  issues: K8sIssue[];
  /** Whether Prometheus recording rules were used for historical metrics */
  usedRecordingRules?: boolean;
  /** Error message if this cluster failed to be processed */
  error?: string;
};

/**
 * Timing breakdown for K8s agent run
 */
export type K8sAgentTimings = {
  /** Time to parse configuration */
  configParsingMs: number;
  /** Time to collect and analyze metrics from all clusters */
  clusterProcessingMs: number;
  /** Time to create GitHub issues */
  githubIssueCreationMs: number;
  /** Time to close resolved GitHub issues */
  githubIssueClosingMs: number;
  /** Per-cluster timing breakdown */
  perCluster: Array<{
    clusterId: string;
    issueCreationMs: number;
    issueClosingMs: number;
    issueCount: number;
  }>;
};

/**
 * K8s agent result - supports multiple clusters
 */
export type K8sAgentResult = BaseAgentResult & {
  /** Results from each monitored cluster */
  clusters: K8sClusterResult[];
  /** Total issues found across all clusters */
  totalIssues: number;
  /** Performance timing breakdown (optional for backward compatibility) */
  timings?: K8sAgentTimings;
  /** Statistics about skipped items (optional) */
  skipStats?: SkipStats;
  /** Mapping of fingerprints to GitHub issue IDs (optional) */
  duplicateIssueIds?: DuplicateIssueIds;
};

/**
 * Over-provisioned pod with resource suggestions
 */
export type OverprovisionedPod = {
  /** Pod name */
  name: PodName;
  /** Namespace */
  namespace: NamespaceName;
  /** Controller kind (Deployment, ReplicaSet, etc.) */
  controllerKind: ControllerKind | null;
  /** Controller name */
  controllerName: ControllerName | null;
  /** Current CPU limit in cores */
  currentCpuLimit: Cores | null;
  /** Suggested CPU limit in cores */
  suggestedCpuLimit: Cores | null;
  /** Current memory limit in bytes */
  currentMemoryLimit: Bytes | null;
  /** Suggested memory limit in bytes */
  suggestedMemoryLimit: Bytes | null;
  /** CPU savings in cores (current - suggested) */
  cpuSavings: Cores;
  /** Memory savings in bytes (current - suggested) */
  memorySavings: Bytes;
  /** 7-day average CPU usage in millicores (null if no historical data) */
  avgCpuMillicores: Millicores | null;
  /** 7-day average memory usage in bytes (null if no historical data) */
  avgMemoryBytes: Bytes | null;
  /** 7-day P95 CPU usage in millicores (null if no historical data) */
  p95CpuMillicores: Millicores | null;
  /** 7-day P95 memory usage in bytes (null if no historical data) */
  p95MemoryBytes: Bytes | null;
  /** 7-day P99 CPU usage in millicores (null if no historical data) */
  p99CpuMillicores: Millicores | null;
  /** 7-day P99 memory usage in bytes (null if no historical data) */
  p99MemoryBytes: Bytes | null;
};

/**
 * Per-cluster optimization result
 */
export type K8sOptimizationClusterResult = {
  /** Cluster ID */
  clusterId: ClusterId;
  /** Cluster name */
  clusterName: ClusterName;
  /** Prometheus connection status */
  prometheus: {
    connected: boolean;
    /** URL if connected */
    url?: string;
  };
  /** kube-system UID for fingerprinting */
  kubeSystemUid: KubeSystemUid;
  /** Over-provisioned pods detected */
  overprovisionedPods: OverprovisionedPod[];
  /** Issues detected (for GitHub issue creation) */
  issues: K8sIssue[];
  /** Error message if this cluster failed */
  error?: string;
  /** Reason why cluster was skipped (e.g., no Prometheus) */
  skippedReason?: string;
};

/**
 * K8s Optimization Agent result - daily Prometheus-based analysis
 */
export type K8sOptimizationAgentResult = BaseAgentResult & {
  /** Results from each monitored cluster */
  clusters: K8sOptimizationClusterResult[];
  /** Total over-provisioned pods across all clusters */
  totalOverprovisionedPods: number;
  /** Total CPU savings in cores */
  totalCpuSavings: Cores;
  /** Total memory savings in bytes */
  totalMemorySavings: Bytes;
  /** Total issues found */
  totalIssues: number;
  /** Statistics about skipped items (optional) */
  skipStats?: SkipStats;
  /** Mapping of fingerprints to GitHub issue IDs (optional) */
  duplicateIssueIds?: DuplicateIssueIds;
};

/**
 * Task error with optional log excerpt for detailed error reporting
 */
export type TaskError = {
  /** Name of the failed task */
  taskName: string;
  /** Name of the stage containing this task */
  stageName?: string;
  /** Error message from timeline issues (often generic like "exited with code 1") */
  message: string;
  /** Extracted error lines from task log (more detailed) */
  logExcerpt?: string;
};

/**
 * Problematic pod information for K8s diagnostics
 */
export type ProblematicPod = {
  /** Pod name */
  name: string;
  /** Pod status (e.g., "CrashLoopBackOff", "ImagePullBackOff") */
  status: string;
  /** Status reason from container state */
  statusReason: string | null;
  /** Status message with details */
  statusMessage: string | null;
  /** Total restart count across all containers */
  restartCount: RestartCount;
  /** Pod phase (Running, Pending, Failed, etc.) */
  phase: string;
  /** Last N lines of pod logs */
  logs: string | null;
  /** Recent pod events formatted */
  events: string | null;
};

/**
 * K8s diagnostics for a failed Helm deployment
 */
export type K8sDiagnostics = {
  /** Cluster ID from vivus k8s_clusters table */
  clusterId: string;
  /** Cluster name for display */
  clusterName: ClusterName;
  /** Target namespace where deployment failed */
  namespace: NamespaceName;
  /** When diagnostics were fetched */
  fetchedAt: string;
  /** How the cluster was matched */
  matchMethod: "namespace_lookup" | "all_clusters_fallback";
  /** Problematic pods found in the namespace */
  pods: ProblematicPod[];
};

import type { PipelineSourceRepo } from "../utils/pipeline-matcher";

/**
 * Individual test result from Azure DevOps Test Management
 */
export type TestResult = {
  testCaseTitle: string;
  outcome: "Failed" | "Passed";
  durationInMs: number;
  automatedTestStorage: string; // File name (e.g., "auth-login.e2e.ts")
  errorMessage?: string;
  stackTrace?: string;
};

/**
 * Test run summary with failed test details
 */
export type TestRunSummary = {
  runId: number;
  runName: string;
  totalCount: number;
  passedCount: number;
  failedCount: number;
  testResultsUrl: string; // Link to Azure DevOps test results
  failedTests: TestResult[]; // Only failed tests (max 5)
};

/**
 * Failed pipeline run with error details and optional K8s diagnostics
 */
export type FailedPipelineRun = {
  projectName: string;
  pipelineId: number;
  pipelineName: string;
  runId: number;
  runUrl: string; // Link to Azure DevOps run detail
  result: "failed"; // Only failed, not canceled
  finishedAt: string;
  // Git context
  sourceRepo?: PipelineSourceRepo;
  sourceBranch: string; // Raw format: "refs/heads/main" or "refs/pull/56/merge"
  sourceVersion: string; // Full commit SHA
  // Build trigger info
  buildReason: string; // "manual" | "batchedCI" | "pullRequest" | "schedule" | ...
  // Error details
  validationErrors?: string[]; // YAML validation errors (before execution)
  taskErrors?: TaskError[]; // Runtime task errors with log excerpts (max 3)
  errorMessage?: string; // Combined error message for backward compatibility
  // K8s diagnostics (for Helm/K8s deployment failures)
  k8sDiagnostics?: K8sDiagnostics;
  // Test results (when E2E tests were published)
  testResults?: TestRunSummary;
};

/**
 * Failed job from a GitHub Actions workflow run
 */
export type FailedWorkflowJob = {
  jobId: number;
  jobName: string;
  conclusion: string;
  failedSteps: Array<{
    stepName: string;
    conclusion: string;
    number: number;
  }>;
};

/**
 * Failed GitHub Actions workflow run with error details
 */
export type FailedWorkflowRun = {
  owner: string;
  repo: string;
  workflowId: number;
  workflowName: string;
  runId: number;
  runNumber: number;
  runAttempt: number;
  runUrl: string;
  conclusion: "failure" | "timed_out";
  headBranch: string;
  headSha: string;
  event: string;
  failedJobs: FailedWorkflowJob[];
  logSnippet?: string;
  errorMessage?: string;
  k8sDiagnostics?: K8sDiagnostics;
};

export type StuckWorkflowJob = {
  jobId: number;
  jobName: string;
  status: "queued" | "waiting" | "requested" | "pending";
  createdAt: string;
  startedAt: string | null;
  runnerId: number | null;
  runnerName: string | null;
  labels: string[];
  queuedDurationMinutes: number;
};

export type StuckWorkflowRun = {
  owner: string;
  repo: string;
  workflowId: number;
  workflowName: string;
  runId: number;
  runNumber: number;
  runAttempt: number;
  runUrl: string;
  status:
    | "in_progress"
    | "queued"
    | "waiting"
    | "requested"
    | "pending";
  headBranch: string;
  headSha: string;
  event: string;
  createdAt: string;
  runStartedAt: string | null;
  stuckJobs: StuckWorkflowJob[];
  queuedDurationMinutes: number;
};

export type TrackedGitHubWorkflow = {
  owner: string;
  repo: string;
  workflowId: number;
  workflowName: string;
  branches: string[];
};

/**
 * Azure DevOps Pipeline Agent Result
 */
export type AzureDevOpsPipelineAgentResult =
  BaseAgentResult & {
    /** Connection ID used for this run */
    connectionId: string;
    /** Time window that was checked - start */
    checkedFrom: string;
    /** Time window that was checked - end */
    checkedTo: string;
    /** Pipelines that are currently failing (latest run is failed) */
    failedRuns: FailedPipelineRun[];
    /** Number of pipelines checked */
    pipelinesChecked: number;
    /** Number of pipelines failing */
    pipelinesFailing: number;
    /** Statistics about skipped items (optional) */
    skipStats?: SkipStats;
    /** Mapping of fingerprints to GitHub issue IDs (optional) */
    duplicateIssueIds?: DuplicateIssueIds;
  };

/**
 * Sentry issue severity level
 */
export type SentryIssueLevel =
  | "error"
  | "fatal"
  | "info"
  | "warning";

/**
 * Type of Sentry check that detected the issue
 */
export type SentryCheckType =
  | "new_error"
  | "performance_issue"
  | "regression"
  | "cron_monitor_failure";

/**
 * GitHub issue creation result for a Sentry issue
 */
export type SentryGitHubIssueResult = {
  url: string;
  status:
    | "created"
    | "skipped_duplicate"
    | "skipped_rate_limit"
    | "skipped_no_repo";
};

/**
 * Source of the suggested assignee determination
 */
export type SentryAssigneeSource =
  | "single_author"
  | "file_match"
  | "last_commit"
  | "fallback";

/**
 * Confidence level of the suggested assignee
 */
export type SentryAssigneeConfidence =
  | "high"
  | "medium"
  | "low";

/**
 * Suggested assignee for a Sentry issue based on code ownership
 */
export type SentrySuggestedAssignee = {
  username: string;
  source: SentryAssigneeSource;
  confidence: SentryAssigneeConfidence;
  /** Human-readable reason for the assignment */
  reason: string;
};

/**
 * Sentry issue detected by the agent
 */
export type SentryIssueResult = {
  id: string;
  shortId: string;
  projectSlug: string;
  title: string;
  culprit: string;
  level: SentryIssueLevel;
  eventCount: number;
  userCount: number;
  firstSeen: string;
  lastSeen: string;
  permalink: string;
  environments?: string[];
  checkType: SentryCheckType;
  githubIssue?: SentryGitHubIssueResult;
  /** Suggested assignee based on code ownership analysis (populated during issue creation) */
  suggestedAssignee?: SentrySuggestedAssignee;
};

/**
 * Statistics about assignee resolution in a Sentry agent run
 */
export type SentryAssigneeStats = {
  /** Number of issues with successfully resolved assignee */
  resolved: number;
  /** Number of issues using fallback (static) assignees */
  fallback: number;
  /** Breakdown by source */
  bySource: {
    single_author: number;
    file_match: number;
    last_commit: number;
    fallback: number;
  };
};

/**
 * Result of a Sentry agent run
 */
export type SentryAgentResult = BaseAgentResult & {
  /** Connection ID used for this run */
  connectionId: string;
  /** Time window that was checked - start */
  checkedFrom: string;
  /** Time window that was checked - end */
  checkedTo: string;
  /** Projects that were checked */
  projectsChecked: string[];
  /** Issues detected during this run */
  issues: SentryIssueResult[];
  /** Number of issues found */
  issuesFound: number;
  /** Number of GitHub issues created */
  issuesCreated: number;
  /** Number of issues skipped (duplicates, rate limits, etc.) */
  issuesSkipped: number;
  /** Number of issues auto-closed */
  issuesClosed: number;
  /** Pagination cursors for resuming checks */
  cursors?: Record<string, string>;
  /** Statistics about assignee resolution */
  assigneeStats?: SentryAssigneeStats;
  /** Statistics about skipped items (optional) */
  skipStats?: SkipStats;
  /** Mapping of fingerprints to GitHub issue IDs (optional) */
  duplicateIssueIds?: DuplicateIssueIds;
};

export type OtelErrorResult = {
  serviceName: string;
  errorMessage: string;
  errorType?: string;
  signal: "log" | "trace" | "both";
  severityText?: string;
  count: number;
  fingerprint: string;
  status:
    | "created"
    | "duplicate"
    | "rate_limited"
    | "skipped"
    | "error";
  skipReason?: string;
};

export type OtelReceiverAgentResult = BaseAgentResult & {
  detectedErrors?: OtelErrorResult[];
  duplicateIssueIds?: DuplicateIssueIds;
  errorsDetected: number;
  issuesCreated: number;
  issuesSkipped: number;
  logsReceived: number;
  skipStats?: SkipStats;
  tracesReceived: number;
};

/**
 * GitHub Actions Agent Result
 */
export type GitHubActionsAgentResult = BaseAgentResult & {
  /** Time window that was checked - start */
  checkedFrom: string;
  /** Time window that was checked - end */
  checkedTo: string;
  trackedRepositories: string[];
  trackedBranches: string[];
  trackedWorkflows: TrackedGitHubWorkflow[];
  /** Workflows that are currently failing */
  failedWorkflows: FailedWorkflowRun[];
  stuckWorkflowRuns?: StuckWorkflowRun[];
  /** Number of repos checked */
  reposChecked: number;
  /** Unique repositories with at least one failing or stuck workflow */
  reposWithIssues?: number;
  /** Number of workflows failing */
  workflowsFailing: number;
  workflowsStuck?: number;
  /** Stuck scan found candidates whose job details could not be loaded */
  stuckWorkflowScanIncomplete?: boolean;
  stuckRunnerScanAt?: string;
  /** Statistics about skipped items */
  skipStats?: SkipStats;
  /** Mapping of fingerprints to GitHub issue IDs */
  duplicateIssueIds?: DuplicateIssueIds;
};

export type AgentRunResult =
  | AzureDevOpsPipelineAgentResult
  | GitHubActionsAgentResult
  | K8sAgentResult
  | OtelReceiverAgentResult
  | SentryAgentResult;

export * from "./ids";
export * from "./otel-types";
